/**
 * 財務管理 API 服務
 * 使用 RTK Query 封裝財務相關的 API 調用
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithErrorHandling } from '@/services/baseApi';
import { IApiResponse, IPaginatedResponse } from '@/types/api';
import {
  IFinanceTransaction,
  IFinanceInvoice,
  IFinanceRefund,
  IFinanceReport,
  IFinanceTransactionParams,
  IFinanceInvoiceParams,
  IFinanceRefundParams,
  IFinanceReportParams,
  ITransactionStatistics,
  IRefundStatistics,
} from '@/types/finance';
import { ConstantUtil } from '@/utils/ConstantUtil';

/**
 * 財務 API 服務
 */
export const financeApi = createApi({
  reducerPath: 'financeApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['FinanceTransaction', 'FinanceInvoice', 'FinanceRefund', 'FinanceReport'],
  endpoints: (builder) => ({
    // 獲取收支明細列表
    getFinanceTransactions: builder.query<
      IApiResponse<IPaginatedResponse<IFinanceTransaction> & { statistics: ITransactionStatistics }>,
      IFinanceTransactionParams
    >({
      query: (params) => ({
        url: ConstantUtil.API_FINANCE_TRANSACTIONS,
        method: 'GET',
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? ConstantUtil.PAGE_SIZE,
          ...(params.type && params.type !== 'all' && { type: params.type }),
          ...(params.status && params.status !== 'all' && { status: params.status }),
          ...(params.startDate && { startDate: params.startDate }),
          ...(params.endDate && { endDate: params.endDate }),
          ...(params.searchKeyword && { searchKeyword: params.searchKeyword }),
          ...(params.sortBy && { sortBy: params.sortBy }),
          ...(params.sortOrder && { sortOrder: params.sortOrder }),
        },
      }),
      providesTags: ['FinanceTransaction'],
    }),

    // 獲取收支明細詳情
    getFinanceTransactionById: builder.query<IApiResponse<IFinanceTransaction>, string>({
      query: (id) => ({
        url: ConstantUtil.API_FINANCE_TRANSACTION_BY_ID(id),
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'FinanceTransaction', id }],
    }),

    // 獲取收支統計數據
    getTransactionStatistics: builder.query<IApiResponse<ITransactionStatistics>, void>({
      query: () => ({
        url: ConstantUtil.API_FINANCE_TRANSACTION_STATISTICS,
        method: 'GET',
      }),
      providesTags: ['FinanceTransaction'],
    }),

    // 獲取發票列表
    getFinanceInvoices: builder.query<
      IApiResponse<IPaginatedResponse<IFinanceInvoice>>,
      IFinanceInvoiceParams
    >({
      query: (params) => ({
        url: ConstantUtil.API_FINANCE_INVOICES,
        method: 'GET',
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? ConstantUtil.PAGE_SIZE,
          ...(params.status && params.status !== 'all' && { status: params.status }),
          ...(params.startDate && { startDate: params.startDate }),
          ...(params.endDate && { endDate: params.endDate }),
          ...(params.searchKeyword && { searchKeyword: params.searchKeyword }),
          ...(params.sortBy && { sortBy: params.sortBy }),
          ...(params.sortOrder && { sortOrder: params.sortOrder }),
        },
      }),
      providesTags: ['FinanceInvoice'],
    }),

    // 獲取發票詳情
    getFinanceInvoiceById: builder.query<IApiResponse<IFinanceInvoice>, string>({
      query: (id) => ({
        url: ConstantUtil.API_FINANCE_INVOICE_BY_ID(id),
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'FinanceInvoice', id }],
    }),

    // 下載發票
    downloadInvoice: builder.mutation<Blob, string>({
      query: (id) => ({
        url: ConstantUtil.API_FINANCE_INVOICE_DOWNLOAD(id),
        method: 'GET',
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    // 獲取退款列表
    getFinanceRefunds: builder.query<
      IApiResponse<IPaginatedResponse<IFinanceRefund> & { statistics: IRefundStatistics }>,
      IFinanceRefundParams
    >({
      query: (params) => ({
        url: ConstantUtil.API_FINANCE_REFUNDS,
        method: 'GET',
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? ConstantUtil.PAGE_SIZE,
          ...(params.status && params.status !== 'all' && { status: params.status }),
          ...(params.startDate && { startDate: params.startDate }),
          ...(params.endDate && { endDate: params.endDate }),
          ...(params.searchKeyword && { searchKeyword: params.searchKeyword }),
          ...(params.sortBy && { sortBy: params.sortBy }),
          ...(params.sortOrder && { sortOrder: params.sortOrder }),
        },
      }),
      providesTags: ['FinanceRefund'],
    }),

    // 獲取退款詳情
    getFinanceRefundById: builder.query<IApiResponse<IFinanceRefund>, string>({
      query: (id) => ({
        url: ConstantUtil.API_FINANCE_REFUND_BY_ID(id),
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'FinanceRefund', id }],
    }),

    // 獲取退款統計數據
    getRefundStatistics: builder.query<IApiResponse<IRefundStatistics>, void>({
      query: () => ({
        url: ConstantUtil.API_FINANCE_REFUND_STATISTICS,
        method: 'GET',
      }),
      providesTags: ['FinanceRefund'],
    }),

    // 獲取財務報表
    getFinanceReports: builder.query<IApiResponse<IFinanceReport>, IFinanceReportParams>({
      query: (params) => ({
        url: ConstantUtil.API_FINANCE_REPORTS,
        method: 'GET',
        params: {
          startDate: params.startDate,
          endDate: params.endDate,
          ...(params.groupBy && { groupBy: params.groupBy }),
        },
      }),
      providesTags: ['FinanceReport'],
    }),

    // 匯出財務報表
    exportFinanceReport: builder.mutation<Blob, IFinanceReportParams>({
      query: (params) => ({
        url: ConstantUtil.API_FINANCE_REPORTS_EXPORT,
        method: 'GET',
        params: {
          startDate: params.startDate,
          endDate: params.endDate,
          ...(params.groupBy && { groupBy: params.groupBy }),
        },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
  }),
});

export const {
  useGetFinanceTransactionsQuery,
  useGetFinanceTransactionByIdQuery,
  useGetTransactionStatisticsQuery,
  useGetFinanceInvoicesQuery,
  useGetFinanceInvoiceByIdQuery,
  useDownloadInvoiceMutation,
  useGetFinanceRefundsQuery,
  useGetFinanceRefundByIdQuery,
  useGetRefundStatisticsQuery,
  useGetFinanceReportsQuery,
  useExportFinanceReportMutation,
} = financeApi;
